import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Autocomplete,
  Tabs,
  Tab,
  Badge,
  Pagination,
  InputAdornment,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,

} from '@mui/material';
import { useForm } from 'react-hook-form';
import {
  Dashboard as DashboardIcon,
  Add as AddIcon,
  Print as PrintIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  LocalHospital as LocalHospitalIcon,
  Security as SecurityIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon,
  QrCode as QrCodeIcon,
  ArrowBack as ArrowBackIcon,
  ContentCopy as ContentCopyIcon,
  Share as ShareIcon,
  MoreVert as MoreVertIcon,
  Remove as RemoveIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { request } from '../../../../../../utils';

// ==================== CONSTANTS ====================
const PURPOSE_OPTIONS = [
  'Medicines',
  'Food & Beverages',
  'Clothing',
  'Documents',
  'Personal Items',
  'Medical Equipment',
  'Valuables',
  'Official Work',
  'Emergency',
  'Other'
];

const STATUS_CONFIG = {
  issued: { label: 'Issued', color: 'warning', icon: <AccessTimeIcon /> },
  used: { label: 'Used', color: 'success', icon: <CheckCircleIcon /> },
  cancelled: { label: 'Cancelled', color: 'error', icon: <CancelIcon /> },
  expired: { label: 'Expired', color: 'default', icon: <WarningIcon /> }
};

const ITEM_CATEGORIES = [
  'medicine',        // Changed from 'Medicines'
  'food',           // Changed from 'Food'
  'clothing',       // Changed from 'Clothing'
  'electronics',    // Changed from 'Electronics'
  'document',       // Changed from 'Documents'
  'valuable',       // Changed from 'Valuables'
  'other'
];

const COMMON_ITEMS = [
  { name: 'Prescription Medicines', category: 'medicine' },
  { name: 'Tablets', category: 'medicine' },
  { name: 'Syrup Bottle', category: 'medicine' },
  { name: 'IV Fluids', category: 'medicine' },
  { name: 'Home Cooked Food', category: 'food' },
  { name: 'Fruits', category: 'food' },
  { name: 'Water Bottles', category: 'food' },
  { name: 'Clothes', category: 'clothing' },
  { name: 'Footwear', category: 'clothing' },
  { name: 'Mobile Phone', category: 'electronics' },
  { name: 'Charger', category: 'electronics' },
  { name: 'Medical Reports', category: 'document' },
  { name: 'X-ray Films', category: 'document' },
  { name: 'Jewelry', category: 'valuable' },
  { name: 'Cash', category: 'valuable' },
  { name: 'Wallet', category: 'valuable' }
];


// ==================== COMPONENTS ====================

// 1. Statistics Card Component
const StatCard = ({ title, value, icon, color, subtext }) => (
  <Card sx={{ height: '100%', bgcolor: `${color}.light`, borderLeft: `4px solid` }}>
    <CardContent>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Avatar sx={{ bgcolor: `${color}.main`, color: 'white' }}>
          {icon}
        </Avatar>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          {subtext && (
            <Typography variant="caption" color="text.secondary">
              {subtext}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

// 2. Item Selector Component
const ItemSelector = ({ items, onItemsChange }) => {
  const [itemName, setItemName] = useState('');
  const [itemCategory, setItemCategory] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [isReturnable, setIsReturnable] = useState(false);

  const addItem = () => {
    if (!itemName.trim()) return;

    // Find if it's a predefined item
    const predefinedItem = COMMON_ITEMS.find(item => item.name === itemName);

    const newItem = {
      id: Date.now(),
      name: itemName,
      category: predefinedItem ? predefinedItem.category : (itemCategory || 'other'),
      quantity: quantity,
      is_returnable: predefinedItem ? (predefinedItem.returnable || false) : isReturnable,
      returned: false
    };

    onItemsChange([...items, newItem]);
    setItemName('');
    setItemCategory('');
    setQuantity(1);
    setIsReturnable(false);
  };

  const removeItem = (id) => {
    onItemsChange(items.filter(item => item.id !== id));
  };

  const updateQuantity = (id, change) => {
    onItemsChange(items.map(item => {
      if (item.id === id) {
        const newQty = item.quantity + change;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }));
  };

  return (
    <Box>
      {/* Add Item Form */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="subtitle2" gutterBottom>
          Add Item
        </Typography>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Autocomplete
              freeSolo
              options={COMMON_ITEMS.map(item => item.name)}
              value={itemName}
              onChange={(event, newValue) => {
                setItemName(newValue || '');
                // If a predefined item is selected, auto-set its category
                if (newValue) {
                  const selectedItem = COMMON_ITEMS.find(item => item.name === newValue);
                  if (selectedItem) {
                    setItemCategory(selectedItem.category); // This sets the category
                    setIsReturnable(selectedItem.returnable || false);
                  }
                }
              }}
              onInputChange={(event, newInputValue) => {
                setItemName(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Item Name"
                  size="small"
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Category"
              value={itemCategory}
              onChange={(e) => setItemCategory(e.target.value)}
            >
              <MenuItem value="">Select</MenuItem>
              {ITEM_CATEGORIES.map(cat => (
                <MenuItem key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)} {/* Capitalize for display */}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={4} sm={2}>
            <TextField
              fullWidth
              size="small"
              label="Qty"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              InputProps={{ inputProps: { min: 1 } }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isReturnable}
                  onChange={(e) => setIsReturnable(e.target.checked)}
                />
              }
              label="Return"
              sx={{ m: 0 }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button
              variant="contained"
              onClick={() => addItem()}
              disabled={!itemName.trim()}
              fullWidth
              size="small"
            >
              Add
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Items List */}
      {items.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Selected Items ({items.length})
          </Typography>
          <Stack spacing={1}>
            {items.map((item) => (
              <Paper key={item.id} sx={{ p: 1.5 }}>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={5}>
                    <Typography variant="body2" fontWeight="medium">
                      {item.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.category}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={() => updateQuantity(item.id, -1)}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography>{item.quantity}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => updateQuantity(item.id, 1)}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Grid>
                  <Grid item xs={2}>
                    <Chip
                      label={item.is_returnable ? "Returnable" : "Consumable"}
                      size="small"
                      color={item.is_returnable ? "warning" : "default"}
                    />
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: 'right' }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeItem(item.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        </Box>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#fafafa' }}>
          <InventoryIcon sx={{ fontSize: 40, color: '#ddd', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No items added
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

// 3. Filter Panel Component
const FilterPanel = ({ filters, onFilterChange }) => {
  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Filters
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Search"
            value={filters.search}
            onChange={(e) => onFilterChange('search', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => onFilterChange('status', e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                <MenuItem key={value} value={value}>
                  {config.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Date"
            type="date"
            value={filters.date}
            onChange={(e) => onFilterChange('date', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

// ==================== MAIN COMPONENT ====================

export const GatePass = () => {
  // State Management
  const [loading, setLoading] = useState(false);
  const [gatePasses, setGatePasses] = useState([]);
  const [filteredPasses, setFilteredPasses] = useState([]);
  const [statistics, setStatistics] = useState({
    total: 0,
    today: 0,
    issued: 0,
    used: 0,
    cancelled: 0
  });

  // Dialog States
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  // Selected Data
  const [selectedPass, setSelectedPass] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Form State
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    date: ''
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Form Hook
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      gate_pass_no: '',
      date: '',
      time: '',
      patient_name: '',
      ipd_no: '',
      uhid: '',
      ward: '',
      room: '',
      bed_no: '',
      care_taker_name: '',
      care_taker_mobile: '',
      care_taker_relation: '',
      purpose: '',
      purpose_details: '',
      out_time: '',
      in_time: '',
      authorized_by: '',
      remarks: '',
      status: 'issued',
      is_urgent: false
    }
  });

  // Initialize
  useEffect(() => {
    fetchData();
    const user = JSON.parse(localStorage.getItem('user'));
    if (user?.name) {
      setValue('authorized_by', user.name);
    }
  }, []);

  // Filter effect
  useEffect(() => {
    filterGatePasses();
  }, [gatePasses, filters]);

  // ==================== API FUNCTIONS ====================

  const fetchData = async () => {
    try {
      setLoading(true);
      const [passesRes, statsRes] = await Promise.all([
        request('/gate-pass-list', { method: 'GET', }),
        request('/gate-pass-statistics', { method: 'GET', })
      ]);

      if (passesRes.success) setGatePasses(passesRes.data || []);
      if (statsRes.success) setStatistics(statsRes.data);
    } catch (error) {
      console.log(error);
      showSnackbar('Error fetching data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const searchPatients = async (searchTerm = '') => {
    try {
      const response = await request('/indoor-registeration-list', {
        method: 'GET',
        query: {
          admission_status: 'admit',
          status: 'active',
          name: searchTerm
        }
      });
      if (response.success) {
        setPatients(response.data || []);
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  // ==================== FILTER FUNCTIONS ====================

  const filterGatePasses = () => {
    let filtered = [...gatePasses];

    // Search filter
    if (filters.search) {
      const term = filters.search.toLowerCase();
      filtered = filtered.filter(pass =>
        pass.gate_pass_no?.toLowerCase().includes(term) ||
        pass.patient_name?.toLowerCase().includes(term) ||
        pass.ipd_no?.toLowerCase().includes(term) ||
        pass.care_taker_name?.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(pass => pass.status === filters.status);
    }

    // Date filter
    if (filters.date) {
      filtered = filtered.filter(pass => pass.date === filters.date);
    }

    setFilteredPasses(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // ==================== DIALOG HANDLERS ====================

  const handleCreateClick = () => {
    reset({
      gate_pass_no: generateGatePassNumber(),
      date: new Date().toISOString().split('T')[0],
      time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
      patient_name: '',
      ipd_no: '',
      uhid: '',
      ward: '',
      room: '',
      bed_no: '',
      care_taker_name: '',
      care_taker_mobile: '',
      care_taker_relation: '',
      purpose: '',
      purpose_details: '',
      out_time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
      in_time: '18:00',
      authorized_by: JSON.parse(localStorage.getItem('user'))?.name || '',
      remarks: '',
      status: 'issued',
      is_urgent: false
    });
    setItems([]);
    setSelectedPatient(null);
    setCreateDialogOpen(true);
  };

  const handleEditClick = (pass) => {
    setSelectedPass(pass);
    Object.keys(pass).forEach(key => {
      if (key in watch()) {
        setValue(key, pass[key]);
      }
    });
    setItems(pass.items || []);
    setEditDialogOpen(true);
  };

  const handleViewClick = (pass) => {
    setSelectedPass(pass);
    setViewDialogOpen(true);
  };

  const handleDeleteClick = (pass) => {
    setSelectedPass(pass);
    setDeleteDialogOpen(true);
  };

  const handlePrintClick = (pass) => {
    setSelectedPass(pass);
    setPrintDialogOpen(true);
  };
  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setValue('patient_name', patient.patient?.name || patient.patient_name || '');
    setValue('ipd_no', patient.ipd_no || '');
    setValue('uhid', patient.uhid || '');
    setValue('ward', patient.ward || patient.current_ward || '');
    setValue('room', patient.room || patient.current_room || '');
    setValue('bed_no', patient.bed_no || '');
    setValue('care_taker_name', patient.care_taker?.name || '');
    setValue('care_taker_mobile', patient.care_taker?.mobile || '');
    setValue('care_taker_relation', patient.care_taker?.relation || '');
    setSearchDialogOpen(false);
  };



  const handleMarkAsUsed = async (passId) => {
    try {
      setLoading(true);
      const response = await request(`/gate-pass-mark-used`, {
        params: passId,
        method: 'PUT',
        body: {
          status: 'used',
          in_time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5)
        }
      });

      if (response.success) {
        showSnackbar('Gate Pass marked as used', 'success');
        fetchData();
      }
    } catch (error) {
      showSnackbar('Error updating gate pass', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ==================== FORM SUBMISSION ====================

  const onSubmitCreate = async (data) => {
    try {
      setLoading(true);

      const gatePassData = {
        ...data,
        items: items,
        patient_reference: selectedPatient?._id || 'temp-patient',
        ipd_reference: selectedPatient?._id || 'temp-ipd',
        createdBy: JSON.parse(localStorage.getItem('user'))?.name || 'System',
        createdAt: new Date().toISOString()
      };

      const response = await request('/gate-pass-create', {
        method: 'POST',
        body: gatePassData
      });

      if (response.success) {
        showSnackbar('Gate Pass created successfully', 'success');
        setCreateDialogOpen(false);
        fetchData();
      } else {
        showSnackbar(response.message || 'Failed to create', 'error');
      }
    } catch (error) {
      showSnackbar('Error creating gate pass', 'error');
    } finally {
      setLoading(false);
    }
  };

  const onSubmitEdit = async (data) => {
    try {
      setLoading(true);

      const gatePassData = {
        ...data,
        items: items,
        updatedAt: new Date().toISOString()
      };

      const response = await request(`/gate-pass-update`, {
        params: selectedPass._id,
        method: 'PUT',
        body: gatePassData
      });

      if (response.success) {
        showSnackbar('Gate Pass updated successfully', 'success');
        setEditDialogOpen(false);
        fetchData();
      }
    } catch (error) {
      showSnackbar('Error updating gate pass', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await request(`/gate-pass-delete/${selectedPass._id}`, {
        method: 'DELETE'
      });

      if (response.success) {
        showSnackbar('Gate Pass deleted successfully', 'success');
        setDeleteDialogOpen(false);
        fetchData();
      }
    } catch (error) {
      showSnackbar('Error deleting gate pass', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ==================== UTILITY FUNCTIONS ====================

  const generateGatePassNumber = () => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `GP${year}${month}${day}${random}`;
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // ==================== RENDER FUNCTIONS ====================

  // 1. Dashboard View
  const renderDashboard = () => (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            <DashboardIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Gate Pass Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage patient gate passes and track entries/exits
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateClick}
        >
          Create New Gate Pass
        </Button>
      </Stack>

      {/* Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Passes"
            value={statistics.total}
            icon={<AssignmentIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Today's Passes"
            value={statistics.today}
            icon={<CalendarIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Issued"
            value={statistics.issued}
            icon={<AccessTimeIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Used"
            value={statistics.used}
            icon={<CheckCircleIcon />}
            color="success"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <FilterPanel filters={filters} onFilterChange={handleFilterChange} />

      {/* Gate Passes Table */}
      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">
              Gate Passes ({filteredPasses.length})
            </Typography>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredPasses.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No gate passes found
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Pass No</TableCell>
                    <TableCell>Patient</TableCell>
                    <TableCell>IPD No</TableCell>
                    <TableCell>Care Taker</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPasses.slice(0, 10).map((pass) => (
                    <TableRow key={pass._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {pass.gate_pass_no}
                        </Typography>
                      </TableCell>
                      <TableCell>{pass.patient_name}</TableCell>
                      <TableCell>{pass.ipd_no}</TableCell>
                      <TableCell>{pass.care_taker_name}</TableCell>
                      <TableCell>{pass.out_time}</TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_CONFIG[pass.status]?.label || pass.status}
                          size="small"
                          color={STATUS_CONFIG[pass.status]?.color || 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => handleViewClick(pass)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Print">
                            <IconButton
                              size="small"
                              onClick={() => handlePrintClick(pass)}
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {pass.status === 'issued' && (
                            <>
                              <Tooltip title="Mark as Used">
                                <IconButton
                                  size="small"
                                  onClick={() => handleMarkAsUsed(pass._id)}
                                >
                                  <CheckCircleIcon fontSize="small" color="success" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditClick(pass)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(pass)}
                                >
                                  <DeleteIcon fontSize="small" color="error" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  // 2. Create/Edit Form Dialog
  const renderFormDialog = (isEdit = false) => {
    const dialogOpen = isEdit ? editDialogOpen : createDialogOpen;
    const setDialogOpen = isEdit ? setEditDialogOpen : setCreateDialogOpen;
    const onSubmit = isEdit ? onSubmitEdit : onSubmitCreate;
    const title = isEdit ? 'Edit Gate Pass' : 'Create New Gate Pass';

    return (
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LocalHospitalIcon color="primary" />
            <Typography variant="h6">{title}</Typography>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <form id="gate-pass-form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              {/* Basic Info */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Gate Pass No"
                  {...register('gate_pass_no')}
                  disabled
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  {...register('date', { required: 'Date is required' })}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.date}
                  helperText={errors.date?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Time"
                  type="time"
                  {...register('time', { required: 'Time is required' })}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.time}
                  helperText={errors.time?.message}
                />
              </Grid>

              {/* Patient Selection */}
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={() => setSearchDialogOpen(true)}
                  fullWidth
                >
                  {selectedPatient ? `Selected: ${selectedPatient.patient_name || selectedPatient.ipd_no}` : 'Select Patient'}
                </Button>
              </Grid>

              {/* Patient Details */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Patient Name"
                  {...register('patient_name', { required: 'Patient name is required' })}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.patient_name}
                  helperText={errors.patient_name?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="IPD No"
                  {...register('ipd_no', { required: 'IPD number is required' })}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.ipd_no}
                  helperText={errors.ipd_no?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="UHID"
                  {...register('uhid')}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Care Taker Details */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Care Taker Name"
                  {...register('care_taker_name', { required: 'Care taker name is required' })}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.care_taker_name}
                  helperText={errors.care_taker_name?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  {...register('care_taker_mobile', {
                    required: 'Mobile number is required',
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: 'Enter valid 10-digit number'
                    }
                  })}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.care_taker_mobile}
                  helperText={errors.care_taker_mobile?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Relation"
                  {...register('care_taker_relation', { required: 'Relation is required' })}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.care_taker_relation}
                  helperText={errors.care_taker_relation?.message}
                />
              </Grid>

              {/* Gate Pass Timing */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Out Time"
                  type="time"
                  {...register('out_time', { required: 'Out time is required' })}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.out_time}
                  helperText={errors.out_time?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Expected In Time"
                  type="time"
                  {...register('in_time')}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Purpose */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" error={!!errors.purpose}>
                  <InputLabel>Purpose</InputLabel>
                  <Select
                    {...register('purpose', { required: 'Purpose is required' })}
                    label="Purpose"
                  >
                    <MenuItem value="">Select Purpose</MenuItem>
                    {PURPOSE_OPTIONS.map(purpose => (
                      <MenuItem key={purpose} value={purpose}>{purpose}</MenuItem>
                    ))}
                  </Select>
                  {errors.purpose && (
                    <Typography variant="caption" color="error">
                      {errors.purpose.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Purpose Details"
                  {...register('purpose_details')}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Items Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  <InventoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Items Carrying
                </Typography>
                <ItemSelector items={items} onItemsChange={setItems} />
              </Grid>

              {/* Authorized By */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Authorized By"
                  {...register('authorized_by', { required: 'Authorized by is required' })}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.authorized_by}
                  helperText={errors.authorized_by?.message}
                />
              </Grid>

              {/* Remarks */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remarks"
                  {...register('remarks')}
                  multiline
                  rows={2}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Urgent Switch */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch {...register('is_urgent')} />}
                  label="Urgent Gate Pass"
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="gate-pass-form"
            variant="contained"
            disabled={loading}
            startIcon={<SaveIcon />}
          >
            {isEdit ? 'Update' : 'Create'} Gate Pass
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // 3. Patient Search Dialog
  const renderSearchDialog = () => (
    <Dialog
      open={searchDialogOpen}
      onClose={() => setSearchDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Select Patient</Typography>
          <IconButton onClick={() => setSearchDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          fullWidth
          label="Search by IPD No, UHID, or Name"
          onChange={(e) => {
            setSearchTerm(e.target.value);
            searchPatients(e.target.value);
          }}
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />

        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
          {patients.map((patient) => (
            <ListItem
              key={patient._id}
              button
              onClick={() => {
                handlePatientSelect(patient);
                setSearchDialogOpen(false);
              }}
            >
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText
                primary={patient.patient?.name || patient.patient_name}
                secondary={`IPD: ${patient.ipd_no} | UHID: ${patient.uhid} | Ward: ${patient.ward}`}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );

  // 4. View Details Dialog
  const renderViewDialog = () => (
    <Dialog
      open={viewDialogOpen}
      onClose={() => setViewDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Gate Pass Details</Typography>
          <IconButton onClick={() => setViewDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {selectedPass && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{selectedPass.gate_pass_no}</Typography>
                <Chip
                  label={STATUS_CONFIG[selectedPass.status]?.label || selectedPass.status}
                  color={STATUS_CONFIG[selectedPass.status]?.color || 'default'}
                />
              </Stack>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Patient:
              </Typography>
              <Typography variant="body1">{selectedPass.patient_name}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                IPD No:
              </Typography>
              <Typography variant="body1">{selectedPass.ipd_no}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Care Taker:
              </Typography>
              <Typography variant="body1">{selectedPass.care_taker_name}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Mobile:
              </Typography>
              <Typography variant="body1">{selectedPass.care_taker_mobile}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Out Time:
              </Typography>
              <Typography variant="body1">{selectedPass.out_time}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                In Time:
              </Typography>
              <Typography variant="body1">{selectedPass.in_time || 'N/A'}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Purpose:
              </Typography>
              <Typography variant="body1">{selectedPass.purpose}</Typography>
              {selectedPass.purpose_details && (
                <Typography variant="body2">{selectedPass.purpose_details}</Typography>
              )}
            </Grid>

            {selectedPass.items && selectedPass.items.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Items:
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>Returnable</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedPass.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.category}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>
                            <Chip
                              label={item.is_returnable ? 'Yes' : 'No'}
                              size="small"
                              color={item.is_returnable ? 'warning' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            {selectedPass.remarks && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Remarks:
                </Typography>
                <Typography variant="body1">{selectedPass.remarks}</Typography>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={() => {
            setViewDialogOpen(false);
            setPrintDialogOpen(true);
          }}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );

  // 5. Delete Confirmation Dialog
  const renderDeleteDialog = () => (
    <Dialog
      open={deleteDialogOpen}
      onClose={() => setDeleteDialogOpen(false)}
    >
      <DialogTitle>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to delete gate pass <strong>{selectedPass?.gate_pass_no}</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDeleteConfirm}
          disabled={loading}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderPrintDialog = () => (
    <Dialog
      open={printDialogOpen}
      onClose={() => setPrintDialogOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogContent sx={{ p: 1 }}>
        {selectedPass && (
          <Box
            sx={{
              p: 2,
              border: '2px solid #000',
              borderRadius: 1,
              width: '100%',
              maxWidth: '400px',
              margin: '0 auto',
              position: 'relative'
            }}
          >
            {/* Header Section */}
            <Box sx={{ textAlign: 'center', mb: 1.5, borderBottom: '1px solid #ccc', pb: 1 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem', lineHeight: 1.2 }}>
                HOSPITAL GATE PASS
              </Typography>
              <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                {selectedPass.gate_pass_no}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption">
                  <strong>Date:</strong> {new Date(selectedPass.date).toLocaleDateString()}
                </Typography>
                <Typography variant="caption">
                  <strong>Time:</strong> {selectedPass.time}
                </Typography>
              </Box>
            </Box>

            {/* Patient Info - Compact */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                PATIENT INFO
              </Typography>
              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                    <strong>Name:</strong> {selectedPass.patient_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                    <strong>IPD:</strong> {selectedPass.ipd_no}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                    <strong>UHID:</strong> {selectedPass.uhid}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Care Taker - Compact */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                CARETAKER
              </Typography>
              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                    <strong>Name:</strong> {selectedPass.care_taker_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                    <strong>Relation:</strong> {selectedPass.care_taker_relation}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                    <strong>Mobile:</strong> {selectedPass.care_taker_mobile}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Items Section - Compact */}
            {selectedPass.items && selectedPass.items.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                  ITEMS CARRYING ({selectedPass.items.length})
                </Typography>
                <Box sx={{ maxHeight: '100px', overflowY: 'auto', bgcolor: '#f9f9f9', p: 0.5, borderRadius: 0.5 }}>
                  {selectedPass.items.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.7rem',
                        py: 0.25,
                        borderBottom: index < selectedPass.items.length - 1 ? '1px dotted #ddd' : 'none'
                      }}
                    >
                      <span>
                        {item.name} ({item.quantity})
                      </span>
                      <Chip
                        label={item.is_returnable ? "R" : "C"}
                        size="small"
                        color={item.is_returnable ? "warning" : "default"}
                        sx={{ height: '16px', fontSize: '0.6rem' }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Timing */}
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'block' }}>
                  <strong>OUT:</strong> {selectedPass.out_time}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'block' }}>
                  <strong>IN:</strong> {selectedPass.in_time || '--:--'}
                </Typography>
              </Box>
              {selectedPass.is_urgent && (
                <Chip
                  label="URGENT"
                  color="error"
                  size="small"
                  sx={{ height: '20px', fontSize: '0.6rem' }}
                />
              )}
              <Chip
                label={STATUS_CONFIG[selectedPass.status]?.label || selectedPass.status}
                size="small"
                color={STATUS_CONFIG[selectedPass.status]?.color || 'default'}
                sx={{ height: '20px', fontSize: '0.6rem' }}
              />
            </Box>

            {/* Purpose */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'block' }}>
                <strong>Purpose:</strong> {selectedPass.purpose}
              </Typography>
              {selectedPass.purpose_details && (
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {selectedPass.purpose_details}
                </Typography>
              )}
            </Box>

            {/* Signatures - Compact */}
            <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #000' }}>
              <Grid container spacing={1}>
                <Grid item xs={4} sx={{ textAlign: 'center' }}>
                  <Box sx={{ height: '40px', borderBottom: '1px solid #000', mb: 0.5 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                    Care Taker
                  </Typography>
                </Grid>
                <Grid item xs={4} sx={{ textAlign: 'center' }}>
                  <Box sx={{ height: '40px', borderBottom: '1px solid #000', mb: 0.5 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                    Security
                  </Typography>
                </Grid>
                <Grid item xs={4} sx={{ textAlign: 'center' }}>
                  <Box sx={{ height: '40px', borderBottom: '1px solid #000', mb: 0.5 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1 }}>
                    Auth: {selectedPass.authorized_by}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                Valid for exit: {selectedPass.out_time} | Return by: {selectedPass.in_time || '--:--'}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 1.5 }}>
        <Button
          onClick={() => setPrintDialogOpen(false)}
          size="small"
          startIcon={<CloseIcon />}
        >
          Close
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<PrintIcon />}
          onClick={() => {
            const printContent = document.querySelector('.MuiDialogContent-root');
            const originalContent = document.body.innerHTML;
            document.body.innerHTML = printContent.innerHTML;
            window.print();
            document.body.innerHTML = originalContent;
            window.location.reload();
          }}
        >
          Print Pass
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ==================== MAIN RENDER ====================

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Dashboard Content */}
      {renderDashboard()}

      {/* All Dialogs */}
      {renderFormDialog(false)} {/* Create Dialog */}
      {renderFormDialog(true)}  {/* Edit Dialog */}
      {renderSearchDialog()}
      {renderViewDialog()}
      {renderDeleteDialog()}
      {renderPrintDialog()}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};